/**
 * Form tracking utilities for GTM dataLayer events
 * Tracks form_start, form_submit, and form_error events
 */

import { track } from './index';

// Tracks which forms have fired form_start this session (per page load)
const formStartFired = new Set<string>();

type SubmitMethod = 'ajax' | 'post';
type SubmitStatus = 'success' | 'failure';
type ErrorType = 'required' | 'invalid_format' | 'server_error';

/**
 * Track when a user first interacts with a form (first focus)
 * Only fires once per form per page session
 */
export const trackFormStart = (formId: string, pageSection: string): void => {
  if (formStartFired.has(formId)) {
    return;
  }

  formStartFired.add(formId);

  track('form_start', {
    form_id: formId,
    page_section: pageSection,
  });
};

/**
 * Track form submission (success or failure)
 */
export const trackFormSubmit = (
  formId: string,
  submitMethod: SubmitMethod,
  status: SubmitStatus,
): void => {
  track('form_submit', {
    form_id: formId,
    submit_method: submitMethod,
    status,
  });
};

/**
 * Track form validation or submission errors
 * @param formId - The form identifier
 * @param fieldName - The field that has the error (e.g., 'email', 'phone')
 * @param errorType - The type of error: 'required', 'invalid_format', or 'server_error'
 */
export const trackFormError = (
  formId: string,
  fieldName: string,
  errorType: ErrorType,
): void => {
  track('form_error', {
    form_id: formId,
    field_name: fieldName,
    error_type: errorType,
  });
};

/**
 * Reset form tracking state (useful for testing or SPA navigation)
 */
export const resetFormTracking = (): void => {
  formStartFired.clear();
};

/**
 * Create a focusin handler for form_start tracking
 * Returns a handler that can be attached to a form's onFocus event
 */
export const createFormStartHandler = (
  formId: string,
  pageSection: string,
): (() => void) => {
  return () => trackFormStart(formId, pageSection);
};
